import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { useSetCurrentOrgId } from '@/features/org/hooks/use-current-org-id';
import CheckmarkIcon from '@/shared/assets/checkmark.svg?react';
import { DefaultLayout } from '@/shared/components/layouts/default-layout';
import { Gradients } from '@/shared/components/theme/gradient';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/utils/cn';

interface CheckoutSessionDetails {
  planType: string;
  hasTrial: boolean;
  billingPeriod: string;
}

export const CheckoutSuccessPage = () => {
  const { t } = useTranslation('subscriptions');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setCurrentOrgId = useSetCurrentOrgId();
  const [sessionDetails, setSessionDetails] =
    useState<CheckoutSessionDetails | null>(null);

  useEffect(() => {
    const planType = searchParams.get('plan_type');
    const hasTrial = searchParams.get('has_trial') === 'true';
    const billingPeriod = searchParams.get('billing_period');
    const organizationId = searchParams.get('organization_id');

    if (organizationId) {
      setCurrentOrgId(Number(organizationId));
    }

    if (planType && billingPeriod) {
      setSessionDetails({
        planType,
        hasTrial,
        billingPeriod,
      });
    }
  }, [searchParams, setCurrentOrgId]);

  const handleContinue = () => {
    const returnUrl = searchParams.get('return_url');
    if (returnUrl) {
      navigate(returnUrl + '?checkout=true');
    } else {
      navigate(paths.dashboard.getHref());
    }
  };

  return (
    <DefaultLayout>
      <div className="mb-4 flex min-h-[60vh] flex-col items-center justify-center">
        <div
          className={cn(
            'flex w-full max-w-2xl flex-col items-center gap-8 rounded-5xl border border-dashed border-white/15 p-12 shadow-inner',
            Gradients.transparentWhiteGradient,
          )}
        >
          <div
            className={cn(
              'flex size-20 items-center justify-center rounded-full',
              'bg-gradient-to-b from-green-500/20 to-green-600/20',
              'border border-green-500/30',
            )}
          >
            <CheckmarkIcon className="size-10 text-green-500" />
          </div>

          <div className="flex flex-col items-center gap-4 text-center">
            <h1 className="text-4xl font-medium">
              {t('CheckoutSuccessPage.title')}
            </h1>
            <p className="text-lg text-white/70">
              {t('CheckoutSuccessPage.subtitle')}
            </p>
          </div>

          {sessionDetails && (
            <div className="flex w-full flex-col gap-4 rounded-2xl border border-white/10 bg-white/5 p-6">
              <div className="flex flex-col gap-2">
                <h3 className="text-xl font-semibold">
                  {t(`plans.${sessionDetails.planType}.name` as any)}
                </h3>
                <p className="text-sm text-white/60">
                  {t(`billing.billed_${sessionDetails.billingPeriod}` as any)}
                </p>
              </div>

              {sessionDetails.hasTrial ? (
                <div
                  className={cn(
                    'flex flex-col gap-2 rounded-xl border p-4',
                    'bg-gradient-to-b from-purple-500/10 to-purple-600/10',
                    'border-purple-500/30',
                  )}
                >
                  <p className="text-sm font-medium text-purple-300">
                    {t('CheckoutSuccessPage.trial_info.title')}
                  </p>
                  <p className="text-sm text-white/70">
                    {t('CheckoutSuccessPage.trial_info.description')}
                  </p>
                </div>
              ) : (
                <div
                  className={cn(
                    'flex flex-col gap-2 rounded-xl border p-4',
                    'bg-gradient-to-b from-blue-500/10 to-blue-600/10',
                    'border-blue-500/30',
                  )}
                >
                  <p className="text-sm font-medium text-blue-300">
                    {t('CheckoutSuccessPage.payment_info.title')}
                  </p>
                  <p className="text-sm text-white/70">
                    {t('CheckoutSuccessPage.payment_info.description')}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col items-center gap-3 text-center text-sm text-white/60">
            <p>{t('CheckoutSuccessPage.email_confirmation')}</p>
          </div>

          <Button
            variant={'greenGradient'}
            onClick={handleContinue}
            className="min-w-48"
          >
            {searchParams.get('return_url')
              ? t('CheckoutSuccessPage.continue')
              : t('CheckoutSuccessPage.go_to_dashboard')}
          </Button>
        </div>
      </div>
    </DefaultLayout>
  );
};
